// A child's prizes (mockup r02, provisional until the owner picks): real prizes waiting for a
// grown-up, the ones cashed in (a "cashed in" stamp lands on any that are new since last look),
// and what's still in the machine for them. Nothing expires.
import { useEffect, useMemo, useState } from 'react'
import type { Person } from '../../shared/events'
import { Sheet } from '../app/Sheet'
import { kvGet, kvSet } from '../data/db'
import { useEvents } from '../data/progress'
import { myPrizes } from '../domain/rewards'
import { useMotionTier } from '../motion/tier'
import { fmt, t } from '../strings/en'
import { useGrownUpName } from './PlaysSheet'
import { useMachineWords } from './machines'

const dayOf = (ms: number) => fmt.day(new Date(ms).toISOString().slice(0, 10))

export function MyPrizesSheet({ person }: { person: Person }) {
  const words = useMachineWords()
  const events = useEvents()
  const mine = useMemo(() => myPrizes(events, person), [events, person])
  const grownUp = useGrownUpName(person.id)
  const still = useMotionTier() === 'still'
  const seenKey = `cashedSeen:${person.id}`
  const [fresh, setFresh] = useState<Set<string>>(new Set())
  const [open, setOpen] = useState<string | null>(null)
  const noteOf = (p: { title: string; note?: string } | undefined) => (p ? p.note || t.myPrizes.noNote(p.title) : '')

  // Prizes cashed in since this sheet was last open get the stamp once.
  const cashedIds = mine.cashed.map((m) => m.wonId).join(',')
  useEffect(() => {
    if (!cashedIds) return
    void kvGet<string[]>(seenKey).then((seen) => {
      const ids = cashedIds.split(',')
      setFresh(new Set(ids.filter((id) => !(seen ?? []).includes(id))))
      void kvSet(seenKey, ids)
    })
  }, [cashedIds, seenKey])

  return (
    <Sheet title={t.myPrizes.title}>
      {mine.waiting.length ? (
        <>
          <p className="mono sheet-sub">{t.myPrizes.waiting}</p>
          <div className="panel sheet-panel">
            {mine.waiting.map((m) => (
              <div className="row" key={m.wonId}>
                <span className="prize-emoji">{m.prize?.emoji ?? '🎁'}</span>
                <div>
                  <b>{m.prize?.title ?? '?'}</b>
                  <small>{t.myPrizes.wonSub(dayOf(m.at), grownUp)}</small>
                </div>
                <span className="tag yellow">{t.myPrizes.tagWaiting}</span>
              </div>
            ))}
          </div>
        </>
      ) : null}
      {mine.cashed.length ? (
        <>
          <p className="mono sheet-sub">{t.myPrizes.cashed}</p>
          <div className="panel sheet-panel">
            {mine.cashed.map((m) => (
              <div className="row prize-row" key={m.wonId}>
                <span className="prize-emoji">{m.prize?.emoji ?? '🎁'}</span>
                <div>
                  <b>{m.prize?.title ?? '?'}</b>
                  <small>{t.myPrizes.doneSub(dayOf(m.cashedAt!), m.note)}</small>
                </div>
                {fresh.has(m.wonId) && !still ? (
                  // New since last look: the stamp lands where the DONE tag sits.
                  <span className="cashed-stamp">{t.myPrizes.stamp}</span>
                ) : (
                  <span className="tag mint">{t.myPrizes.tagDone}</span>
                )}
              </div>
            ))}
          </div>
        </>
      ) : null}
      {!mine.waiting.length && !mine.cashed.length ? (
        <div className="panel sheet-panel">
          <div className="row">
            <b>{t.myPrizes.none(words)}</b>
          </div>
        </div>
      ) : null}
      <p className="mono sheet-sub split">
        <span>{t.myPrizes.machine}</span>
        <span>{t.myPrizes.forYou}</span>
      </p>
      {/* WI-14: each prize by name; a tap shows the parent's note. */}
      <div className="panel sheet-panel machine-prizes named" aria-label={t.myPrizes.machine}>
        {mine.inMachine.map((p) => (
          <button
            key={p.id}
            className={`prize-chip press${p.paused ? ' paused' : ''}${open === p.id ? ' on' : ''}`}
            aria-expanded={open === p.id}
            onClick={() => setOpen(open === p.id ? null : p.id)}
          >
            <span aria-hidden>{p.emoji}</span> {p.title}
            {p.rarity === 'legendary' ? <span className="tag yellow">{t.prizes.rarity.legendary}</span> : null}
          </button>
        ))}
      </div>
      {open ? <p className="caption prize-note-line">{noteOf(mine.inMachine.find((p) => p.id === open))}</p> : null}
      <p className="caption">{t.myPrizes.note}</p>
    </Sheet>
  )
}
